import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { number as validateCardNumber } from "card-validator";
import { FieldErrors } from "react-hook-form";
import { AvailablePaymentMethod, CartResponse } from "@/networking/carts.api";
import {
  PaymentBehavior,
  SPECIAL_PAYMENT_METHODS,
} from "@/networking/payments.api";
import type {
  CheckoutFormData,
  ContactErrorsState,
} from "@/components/checkout/checkout.types";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatExpirationDate(date: string) {
  // Remove all non-numeric characters except for "/"
  date = date.replace(/[^\d/]/g, "");
  // Limit the length to 7 characters to accommodate MM/YYYY format
  date = date.slice(0, 7);
  // Automatically add "/" between the first 2 and the remaining characters
  if (date.length > 2 && date[2] !== "/") {
    date = date.slice(0, 2) + "/" + date.slice(2);
  }
  return date;
}

export function formatPhoneNumber(phone: string) {
  return phone.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
}

export function formatCardNumber(cardNumber: string) {
  cardNumber = cardNumber.replace(/\D/g, "");

  const cardValidation = validateCardNumber(cardNumber);
  const cardType = cardValidation.card?.type;
  const cardIsValid = cardValidation.isValid;
  let formattedCardNumber = "";
  if (cardValidation.card) {
    const { lengths, gaps } = cardValidation.card;
    const maxLength = Math.max(...lengths);
    cardNumber = cardNumber.slice(0, maxLength);

    const parts: string[] = [];
    let currentPosition = 0;
    gaps.forEach((gap) => {
      parts.push(cardNumber.slice(currentPosition, gap));
      currentPosition = gap;
    });
    parts.push(cardNumber.slice(currentPosition));
    formattedCardNumber = parts.join(" ").trim();
  } else {
    formattedCardNumber = cardNumber.replace(/(\d{4})(?=\d)/g, "$1 ").trim();
  }

  return {
    formattedCardNumber,
    cardType,
    cardIsValid,
  };
}

export function formatCVC(cvc: string) {
  return cvc.replace(/\D/g, "").slice(0, 4);
}

export function formatSocialSecurityNumber(ssn: string) {
  ssn = ssn.replace(/\D/g, "").slice(0, 9);
  return ssn.replace(/(\d{3})(\d{2})(\d{4})/, "$1-$2-$3");
}

export function validateExpirationDate(date: string) {
  const errors: string[] = [];

  if (date.length < 5) {
    errors.push("please_enter_valid_expiration_date");
    return errors;
  }

  if (date.length === 5 && date.includes("/")) {
    const parts = date.split("/");
    const month = parts[0];
    const year = parts[1];

    if (month && year) {
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear() % 100;
      const currentMonth = currentDate.getMonth() + 1;

      const monthNum = parseInt(month, 10);
      const yearNum = parseInt(year, 10);

      if (isNaN(monthNum) || isNaN(yearNum)) {
        errors.push("invalid_date_format");
      } else if (monthNum < 1 || monthNum > 12) {
        errors.push("invalid_month");
      } else if (
        yearNum < currentYear ||
        (yearNum === currentYear && monthNum < currentMonth)
      ) {
        errors.push("card_has_expired");
      }
    }
  }

  return errors;
}

export function validateCVC(cvc: string, cardType: string) {
  const errors: string[] = [];
  const expectedLength = cardType === "amex" ? 4 : 3;

  if (cvc.length < expectedLength) {
    errors.push(
      expectedLength === 4 ? "cvc_must_be_4_digits" : "cvc_must_be_3_digits",
    );
  }

  return errors;
}

export function validateName(name: string) {
  const errors: string[] = [];

  if (name.trim().length < 2) {
    errors.push("name_must_be_at_least_2_characters");
  }

  return errors;
}

export function validateZipCode(zip: string) {
  const errors: string[] = [];

  if (zip.trim().length < 3) {
    errors.push("please_enter_valid_zip_code");
  }

  return errors;
}

export function formatName(name: string) {
  // Remove numbers and special characters, allow only letters, spaces, hyphens, and apostrophes
  return name.replace(/[^a-zA-Z\s'-]/g, "");
}

export function formatZipCode(zip: string) {
  // Allow only alphanumeric characters for zip codes (supports both US and international formats)
  return zip.replace(/[^a-zA-Z0-9\s-]/g, "").slice(0, 10);
}

export function formatInputValue(value: string, type: string) {
  switch (type) {
    case "Mobile phone number":
      value = formatPhoneNumber(value);
      break;
    case "MM / YY":
      value = formatExpirationDate(value);
      break;
    case "Card Number":
      value = formatCardNumber(value).formattedCardNumber;
      break;
    case "CVC":
      value = formatCVC(value);
      break;
    case "Social Security Number":
      value = formatSocialSecurityNumber(value);
      break;
    case "Name":
      value = formatName(value);
      break;
    case "Zip Code":
      value = formatZipCode(value);
      break;
  }
  return value;
}

export function formatPrice(price: string) {
  return price.replace(/\s*\(.*?\)\s*/g, "");
}

export type DecodedJWT = {
  user_company_id: number | null;
  full_name: string;
  email: string;
  exp: number;
  id: number;
};

export function decodeJWT(token?: string | null): DecodedJWT | null {
  if (!token) return null;
  try {
    const [, payloadBase64] = token.split(".");

    if (!payloadBase64) {
      throw new Error("Invalid token structure");
    }

    const payload = atob(payloadBase64.replace(/-/g, "+").replace(/_/g, "/"));

    const parsedPayload = JSON.parse(payload);

    const expirationTime = parsedPayload.exp as number;
    if (expirationTime && Date.now() >= expirationTime * 1000) {
      return null;
    }

    return parsedPayload;
  } catch (error) {
    console.error("Failed to decode JWT:", error);
    return null;
  }
}

export function toIntlLocale(language: string | undefined): string | undefined {
  return language ? language.replace(/_/g, "-") : undefined;
}

export function formatDate(
  dateString: string,
  isCancelled: boolean = false,
  language?: string,
) {
  if (isCancelled) return "Cancelled";
  if (!dateString) return "Paused";

  const parts = dateString.split("-");
  if (parts.length !== 3) return "Invalid date";

  const [year, month, day] = parts.map(Number);
  if (!year || !month || !day) return "Invalid date";
  const date = new Date(year, month - 1, day); // month is 0-indexed

  return new Intl.DateTimeFormat(toIntlLocale(language), {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
}

type SkipForPending = { skipped_date: string; unskipped_at?: string | null };

// returns the skip that can still be undone (unskipped, date not passed), if any. Uses UTC dates to match backend.
export function getPendingSkip(
  skips: SkipForPending[] | null | undefined,
): SkipForPending | undefined {
  if (!skips?.length) return undefined;
  const todayUTC = new Date(new Date().toISOString().slice(0, 10));
  return skips.find((s) => {
    if (s.unskipped_at != null) return false;
    const skipDate = new Date(s.skipped_date.slice(0, 10));
    return skipDate.getTime() >= todayUTC.getTime();
  });
}

type TranslationFn = (key: string) => string;

export function formatDateTime(
  dateString: string,
  t: TranslationFn,
  language?: string,
): string {
  const date = new Date(dateString);
  const locale = toIntlLocale(language);

  const datePart = new Intl.DateTimeFormat(locale, {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
  }).format(date);

  const timePart = new Intl.DateTimeFormat(locale, {
    hour: "numeric",
    minute: "2-digit",
  }).format(date);

  return `${datePart} ${t("at_time")} ${timePart}`;
}

export function formatSSN(ssn: string) {
  const digitsOnly = ssn.replace(/\D/g, "");

  const limitedDigits = digitsOnly.slice(0, 9);

  if (limitedDigits.length <= 3) {
    return limitedDigits;
  } else if (limitedDigits.length <= 5) {
    return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3)}`;
  } else {
    return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(
      3,
      5,
    )}-${limitedDigits.slice(5)}`;
  }
}

type Item = {
  allow_subscription: boolean;
  subscription_interval: number | null;
  subscription_price_in_currency: string | null;
};

export function getSubscriptionPrice(item: Item) {
  if (!item.allow_subscription) return null;
  const intervalText =
    item.subscription_interval === 1
      ? "month"
      : `${item.subscription_interval} mo.`;
  return `${item.subscription_price_in_currency}/${intervalText}`;
}

export const usStates = [
  { name: "Alabama", isoCode: "AL" },
  { name: "Alaska", isoCode: "AK" },
  { name: "Arizona", isoCode: "AZ" },
  { name: "Arkansas", isoCode: "AR" },
  { name: "California", isoCode: "CA" },
  { name: "Colorado", isoCode: "CO" },
  { name: "Connecticut", isoCode: "CT" },
  { name: "Delaware", isoCode: "DE" },
  { name: "Florida", isoCode: "FL" },
  { name: "Georgia", isoCode: "GA" },
  { name: "Hawaii", isoCode: "HI" },
  { name: "Idaho", isoCode: "ID" },
  { name: "Illinois", isoCode: "IL" },
  { name: "Indiana", isoCode: "IN" },
  { name: "Iowa", isoCode: "IA" },
  { name: "Kansas", isoCode: "KS" },
  { name: "Kentucky", isoCode: "KY" },
  { name: "Louisiana", isoCode: "LA" },
  { name: "Maine", isoCode: "ME" },
  { name: "Maryland", isoCode: "MD" },
  { name: "Massachusetts", isoCode: "MA" },
  { name: "Michigan", isoCode: "MI" },
  { name: "Minnesota", isoCode: "MN" },
  { name: "Mississippi", isoCode: "MS" },
  { name: "Missouri", isoCode: "MO" },
  { name: "Montana", isoCode: "MT" },
  { name: "Nebraska", isoCode: "NE" },
  { name: "Nevada", isoCode: "NV" },
  { name: "New Hampshire", isoCode: "NH" },
  { name: "New Jersey", isoCode: "NJ" },
  { name: "New Mexico", isoCode: "NM" },
  { name: "New York", isoCode: "NY" },
  { name: "North Carolina", isoCode: "NC" },
  { name: "North Dakota", isoCode: "ND" },
  { name: "Ohio", isoCode: "OH" },
  { name: "Oklahoma", isoCode: "OK" },
  { name: "Oregon", isoCode: "OR" },
  { name: "Pennsylvania", isoCode: "PA" },
  { name: "Rhode Island", isoCode: "RI" },
  { name: "South Carolina", isoCode: "SC" },
  { name: "South Dakota", isoCode: "SD" },
  { name: "Tennessee", isoCode: "TN" },
  { name: "Texas", isoCode: "TX" },
  { name: "Utah", isoCode: "UT" },
  { name: "Vermont", isoCode: "VT" },
  { name: "Virginia", isoCode: "VA" },
  { name: "Washington", isoCode: "WA" },
  { name: "West Virginia", isoCode: "WV" },
  { name: "Wisconsin", isoCode: "WI" },
  { name: "Wyoming", isoCode: "WY" },
];

export const convertPriceToPositiveInteger = (price: string): number => {
  if (!price) return 0;
  const floatPrice = parseFloat(price.replace(/[^0-9.-]+/g, ""));
  return Math.round(floatPrice * 100);
};

/**
 * Formats a raw numeric amount for payment APIs (Apple Pay, PayPal, Braintree).
 * Expects a clean number from the BE — no locale parsing needed.
 */
export function toPaymentAmount(
  value: number | undefined | null,
  decimalPlaces?: number | null,
): string {
  const dp = decimalPlaces ?? 2;
  const num = value ?? 0;
  return dp === 0 ? Math.round(num).toString() : num.toFixed(dp);
}

export const formatSubscriptionFrequency = (
  unit: string,
  interval: number = 1,
  t?: (
    key: string,
    fallback?: string,
    params?: Record<string, string | number>,
  ) => string,
) => {
  if (interval === 1) {
    if (t) {
      return t("every_frequency", undefined, { frequency: t(unit) });
    }
    if (unit === "day") return "Daily";
    if (unit === "week") return "Weekly";
    if (unit === "month") return "Monthly";
    if (unit === "year") return "Yearly";
    return unit;
  }

  const pluralUnit = t ? t(`${unit}s`) : `${unit}s`;
  if (t) {
    return t("every_interval_frequency", undefined, {
      interval,
      frequency: pluralUnit,
    });
  }
  return `Every ${interval} ${pluralUnit}`;
};

export const capitalizeFirstLetter = (value: string) => {
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

export const calculateResumeDate = (
  billingInterval: number,
  billingIntervalUnit: string,
  orderCount: number,
  fromDate?: string | Date | null,
): Date => {
  let startDate: Date;

  if (fromDate) {
    if (typeof fromDate === "string") {
      startDate = new Date(
        fromDate + (fromDate.includes("T") ? "" : "T12:00:00.000Z"),
      );
    } else {
      startDate = new Date(
        Date.UTC(
          fromDate.getUTCFullYear(),
          fromDate.getUTCMonth(),
          fromDate.getUTCDate(),
          12,
          0,
          0,
          0,
        ),
      );
    }
  } else {
    const now = new Date();
    startDate = new Date(
      Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate(),
        12,
        0,
        0,
        0,
      ),
    );
  }

  const baseDate = new Date(startDate.getTime());
  const totalIntervals = billingInterval * orderCount;

  switch (billingIntervalUnit.toLowerCase()) {
    case "day":
      baseDate.setUTCDate(baseDate.getUTCDate() + totalIntervals);
      break;
    case "week":
      baseDate.setUTCDate(baseDate.getUTCDate() + totalIntervals * 7);
      break;
    case "month":
      const currentMonth = baseDate.getUTCMonth();
      const currentDay = baseDate.getUTCDate();
      baseDate.setUTCMonth(currentMonth + totalIntervals);

      if (baseDate.getUTCDate() !== currentDay) {
        baseDate.setUTCDate(0);
      }
      break;
    case "year":
      baseDate.setUTCFullYear(baseDate.getUTCFullYear() + totalIntervals);
      break;
    default:
      const defaultMonth = baseDate.getUTCMonth();
      const defaultDay = baseDate.getUTCDate();
      baseDate.setUTCMonth(defaultMonth + totalIntervals);

      if (baseDate.getUTCDate() !== defaultDay) {
        baseDate.setUTCDate(0);
      }
  }

  return baseDate;
};

export function calculateNextBillDate(
  billingInterval: number = 1,
  billingIntervalUnit: string = "month",
): string {
  const now = new Date();
  const nextBillDate = new Date(now);

  switch (billingIntervalUnit.toLowerCase()) {
    case "day":
      nextBillDate.setDate(now.getDate() + billingInterval);
      break;
    case "week":
      nextBillDate.setDate(now.getDate() + billingInterval * 7);
      break;
    case "month":
      nextBillDate.setMonth(now.getMonth() + billingInterval);
      break;
    case "year":
      nextBillDate.setFullYear(now.getFullYear() + billingInterval);
      break;
    default:
      // Default to monthly if unknown unit
      nextBillDate.setMonth(now.getMonth() + billingInterval);
  }

  // Format as YYYY-MM-DD
  return nextBillDate.toISOString().split("T")[0] || "";
}

export function formatCurrency(
  value: number,
  options?: { symbol?: string; currencyCode?: string },
): string {
  const safeValue =
    value != null && typeof value === "number" && !Number.isNaN(value)
      ? value
      : 0;
  if (options?.currencyCode) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: options.currencyCode,
    }).format(safeValue);
  }
  const formatted = safeValue.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (options?.symbol != null) {
    return `${options.symbol}${formatted}`;
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(safeValue);
}

// Calculate total price by multiplying unit price by quantity
export function calculateTotalPrice(unitPrice: number, qty: number): string {
  return (unitPrice * qty).toFixed(2);
}

export const getNextBillDisplay = <
  T extends { status?: string; next_bill_date?: string | null },
>(
  subscription: T | null | undefined,
) => {
  if (subscription?.status === "cancelled") return "Cancelled";
  if (subscription?.next_bill_date)
    return formatDate(subscription.next_bill_date);
  if (subscription?.status === "paused") return "Paused";
  return "N/A";
};

// Calculate subscription price by removing the discount amount
export function calculateSubscriptionPrice(
  totalPrice: number,
  subscribeAndSave: string | null | undefined,
  quantity: number,
): string {
  if (!subscribeAndSave) return totalPrice.toFixed(2);

  const unitDiscountAmount = parseFloat(subscribeAndSave);
  if (isNaN(unitDiscountAmount)) return totalPrice.toFixed(2);

  const subscriptionAmount = totalPrice - unitDiscountAmount * quantity;
  return subscriptionAmount.toFixed(2);
}

// Calculate total savings amount
export function calculateTotalSavings(
  subscribeAndSave: string | null | undefined,
  quantity: number,
): number {
  if (!subscribeAndSave) return 0;

  const unitDiscountAmount = parseFloat(subscribeAndSave);
  if (isNaN(unitDiscountAmount)) return 0;

  return Number((unitDiscountAmount * quantity).toFixed(2));
}

// Recurring items calculation functions
export function getRecurringItems(
  cart: CartResponse["cart"] | null | undefined,
) {
  return cart?.recurring?.filter((recurring) => recurring.subtotal !== 0) || [];
}

export function calculateTotalRecurringAmount(
  recurringItems: NonNullable<CartResponse["cart"]["recurring"]>,
) {
  return recurringItems.reduce((total, recurring) => {
    return total + (recurring.subtotal ?? 0);
  }, 0);
}

export function getCurrencySymbol(
  recurringItems: NonNullable<CartResponse["cart"]["recurring"]>,
) {
  return (
    recurringItems[0]?.subtotal_for_display?.match(/^[^\d\s]+/)?.[0] || "$"
  );
}

export function getEarliestStartDate(
  recurringItems: NonNullable<CartResponse["cart"]["recurring"]>,
) {
  return recurringItems
    .filter((item) => item.start_date)
    .map((item) => new Date(item.start_date!))
    .sort((a, b) => a.getTime() - b.getTime())[0];
}

// payment-utils
export function getPaymentBehavior(paymentType: string): PaymentBehavior {
  // Check if it's a special payment method
  if (paymentType in SPECIAL_PAYMENT_METHODS) {
    return SPECIAL_PAYMENT_METHODS[
      paymentType as keyof typeof SPECIAL_PAYMENT_METHODS
    ];
  }

  // Default behavior for ANY unknown payment method is redirect
  return PaymentBehavior.REDIRECT_BASED;
}

export function getPaymentMethodDisplayName(
  availableMethod: AvailablePaymentMethod,
): string {
  return (
    availableMethod.name ||
    availableMethod.type
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase())
  );
}

export function getPaymentMethodButtonText(
  paymentType: string,
  methodName?: string,
): string {
  const behavior = getPaymentBehavior(paymentType);

  switch (behavior) {
    case PaymentBehavior.CREDIT_CARD:
      return "Pay Now";
    case PaymentBehavior.DIRECT_CHECKOUT:
      return "Pay Now";
    default:
      return `Pay with ${
        methodName ||
        paymentType.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
      }`;
  }
}

export const hasShippingAddressErrors = (
  formErrors: FieldErrors<CheckoutFormData>,
) => {
  const shippingErrors = formErrors.customer?.shipping_address;
  const postalCodeError = shippingErrors?.postal_code;
  const hasBlockingPostalCodeError =
    postalCodeError && postalCodeError.type !== "warning";
  return !!(
    shippingErrors?.name ||
    shippingErrors?.address1 ||
    shippingErrors?.city ||
    shippingErrors?.state ||
    hasBlockingPostalCodeError
  );
};

export const hasCreditCardErrors = (
  paymentMethod: string | undefined,
  formErrors: FieldErrors<CheckoutFormData>,
) => {
  if (
    !paymentMethod ||
    getPaymentBehavior(paymentMethod) !== PaymentBehavior.CREDIT_CARD
  )
    return false;

  const creditCardErrors = formErrors.customer?.payment_method;
  const billingPostalCodeError = creditCardErrors?.billing_address?.postal_code;
  const hasBlockingBillingPostalCodeError =
    billingPostalCodeError && billingPostalCodeError.type !== "warning";
  return !!(
    creditCardErrors?.card_number ||
    creditCardErrors?.expiration_date ||
    creditCardErrors?.cvc ||
    creditCardErrors?.first_name ||
    creditCardErrors?.last_name ||
    hasBlockingBillingPostalCodeError ||
    creditCardErrors?.billing_address?.country_code ||
    creditCardErrors?.billing_address?.state ||
    creditCardErrors?.billing_address?.city ||
    creditCardErrors?.billing_address?.address1
  );
};

export const getConsolidatedErrorMessage = (
  t: (
    key: string,
    fallback?: string,
    params?: Record<string, string | number>,
  ) => string,
  contactErrors: ContactErrorsState,
  formErrors: FieldErrors<CheckoutFormData>,
  paymentMethod: string | undefined,
): string | null => {
  const hasContact = !!(
    contactErrors.email ||
    contactErrors.phone ||
    formErrors.customer?.contact_info?.email ||
    formErrors.customer?.contact_info?.phone
  );
  const hasShipping = hasShippingAddressErrors(formErrors);
  const hasPayment = hasCreditCardErrors(paymentMethod, formErrors);

  if (!hasContact && !hasShipping && !hasPayment) return null;

  const sections: string[] = [];
  if (hasContact) sections.push(t("contact").toLowerCase());
  if (hasShipping) sections.push(t("shipping").toLowerCase());
  if (hasPayment) sections.push(t("payment").toLowerCase());

  let sectionText = "";
  if (sections.length === 1) {
    sectionText = sections[0] ?? "";
  } else if (sections.length === 2) {
    sectionText = `${sections[0]} ${t("and")} ${sections[1]}`;
  } else {
    const lastSection = sections.pop() ?? "";
    sectionText = `${sections.join(", ")} ${t("and")} ${lastSection}`;
  }

  return t("please_fix_the_information_before_proceeding", undefined, {
    sections: sectionText,
  });
};

type BillTo = NonNullable<CartResponse["cart"]>["bill_to"] | undefined;

export function formatCondensedBillingAddress(
  billingAddress:
    | {
        address1: string | null;
        city: string | null;
        state: string | null;
        zip: string | null;
      }
    | null
    | undefined,
): string | null {
  if (!billingAddress?.address1) return null;
  const parts = [
    billingAddress.address1,
    [billingAddress.city, billingAddress.state, billingAddress.zip]
      .filter(Boolean)
      .join(", "),
  ].filter(Boolean);
  return parts.join(", ");
}

export function getBillingAddressPayload(billTo: BillTo) {
  return {
    id: billTo?.id,
    name: billTo?.name || "",
    address1: billTo?.address1 || "",
    address2: billTo?.address2 || null,
    city: billTo?.city || "",
    state: billTo?.state || "",
    postal_code: billTo?.postal_code || "",
    country_code: billTo?.country_code || "",
  };
}

/** formats subscription date in subscription timezone (matches fluid-admin). */
export function formatSubscriptionDateParts(
  dateString: string | null | undefined,
  status: string = "",
  timeZone?: string,
  language?: string,
): { date: string; time: string } | string {
  if (status === "cancelled") return "Cancelled";
  // backend allows next_bill_date null only for cancelled or paused (indefinite); so no date => Paused.
  if (!dateString) return "Paused";

  const date = new Date(dateString);

  if (Number.isNaN(date.getTime())) return "N/A";

  let tzOptions: { timeZone: string } | undefined;
  try {
    if (timeZone) {
      Intl.DateTimeFormat(undefined, { timeZone });
      tzOptions = { timeZone };
    }
  } catch {
    tzOptions = { timeZone: "UTC" };
  }

  const locale = toIntlLocale(language);

  const datePart = new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    ...tzOptions,
  }).format(date);

  const timePart = new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    ...tzOptions,
  }).format(date);

  return { date: datePart, time: timePart };
}

/** next order date: next_bill_date + timezone when present, else next_order.bill_date as-is, else N/A. */
export function formatNextOrderDateDisplay(
  subscription: {
    next_bill_date?: string | null;
    next_order?: { bill_date?: string } | null;
    status?: string;
    timezone?: string | null;
  } | null,
  language?: string,
): string {
  if (!subscription) return "N/A";
  const nextBillDate = subscription.next_bill_date;
  const nextOrderBillDate = subscription.next_order?.bill_date;
  if (nextBillDate) {
    const tz = subscription.timezone ?? "UTC";
    const parts = formatSubscriptionDateParts(
      nextBillDate,
      subscription.status ?? "",
      tz,
      language,
    );
    if (typeof parts === "string") return parts;
    return `${parts.date} ${parts.time} (${tz})`;
  }
  if (nextOrderBillDate) return nextOrderBillDate;
  return "N/A";
}

/** next order date as two lines (line1 = date, line2 = time+tz when next_bill_date present). */
export function getNextOrderDateTwoLines(
  subscription: {
    next_bill_date?: string | null;
    next_order?: { bill_date?: string } | null;
    status?: string;
    timezone?: string | null;
  } | null,
  language?: string,
): { line1: string; line2: string } {
  if (!subscription) return { line1: "N/A", line2: "" };
  const nextBillDate = subscription.next_bill_date;
  const nextOrderBillDate = subscription.next_order?.bill_date;
  if (nextBillDate) {
    const tz = subscription.timezone ?? "UTC";
    const parts = formatSubscriptionDateParts(
      nextBillDate,
      subscription.status ?? "",
      tz,
      language,
    );
    if (typeof parts === "string") return { line1: parts, line2: "" };
    return {
      line1: parts.date,
      line2: `${parts.time} (${tz})`,
    };
  }
  if (nextOrderBillDate) return { line1: nextOrderBillDate, line2: "" };
  return { line1: "N/A", line2: "" };
}

export function formatNextBillDate(nextBillDate: string, language?: string) {
  if (!nextBillDate) return "N/A";
  const date = new Date(nextBillDate);
  return new Intl.DateTimeFormat(toIntlLocale(language), {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    timeZone: "UTC",
  }).format(date);
}

export function formatAddress(
  address?: {
    address1?: string | null;
    city?: string | null;
    state?: string | null;
    postal_code?: string | null;
  } | null,
) {
  if (!address) return "N/A";
  const parts = [address.address1, address.city, address.state].filter(Boolean);
  return parts.join(", ") || "N/A";
}

export function getFrequencyLabel(plan?: {
  name?: string;
  billing_interval?: number;
  billing_interval_unit?: string;
}) {
  if (plan?.name) return plan.name;
  if (plan?.billing_interval && plan?.billing_interval_unit) {
    return `${plan.billing_interval} ${plan.billing_interval_unit}`;
  }
  return "N/A";
}

export function splitName(fullName: string): {
  first_name: string;
  last_name: string;
} {
  const trimmed = fullName.trim();
  const lastSpace = trimmed.lastIndexOf(" ");
  if (lastSpace === -1) {
    return { first_name: trimmed, last_name: "" };
  }
  return {
    first_name: trimmed.slice(0, lastSpace),
    last_name: trimmed.slice(lastSpace + 1),
  };
}
